// IND-041 / IND-049: Hoja de recuento (en pruebas): la hoja STP-MP dentro de
// PROA, en paralelo al libro, fila a fila y en su orden, con sus tres tablas
// diferenciadas. La lista se desplaza con la página, sin scroll lateral.
// ACTUAL, COMPRAS y PRECIO se escriben en la fila como en Excel; lo demás, en
// «Editar fila». PROA sugiere; nunca rellena solo.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  addRecountRow,
  applyRecountSuggestions,
  compareRecountSheet,
  deleteRecountRow,
  dismissRecountMaterial,
  exportRecountSheet,
  getIndustrialCostContext,
  getRecountSheet,
  prepareRecountSheetFromBook,
  prepareRecountSheetFromPrevious,
  saveRecountCell,
  saveRecountSupplierCode,
} from '../services/industrialCostService';
import type {
  RecountCompareResult,
  RecountMissingMaterial,
  RecountRowDraft,
  RecountSheetRow,
  RecountSheetView,
} from '../services/industrialCostService';
import { esNumberFormat } from '../utils/numberFormat';
import {
  bookErrorText,
  filterSheetRows,
  fmtQty,
  isTyped,
  mergeSavedRow,
  rowConsumption,
  rowValue,
  replaceLoss,
  sheetSummary,
  suggestionTargets,
  suppliersWithoutCode,
  RECOUNT_TABLES,
  tableOf,
  tableStarts,
} from '../utils/recountSheet';
import type { RecountFilter, RecountTableKind } from '../utils/recountSheet';
import { cellKey, parseCellText, RecountCellSaver } from '../utils/recountCellSaver';
import type { RecountCellField, RecountCellState } from '../utils/recountCellSaver';
import { useElementHeight, useElementSize } from '../utils/useElementHeight';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import { kgDraft, MONTH_NAMES } from '../utils/wasteControl';
import SortableHeaderCell from '../components/SortableHeaderCell';
import { DetailError, DetailLoading } from './IndustrialCostMaterialDetailSections';
import {
  AddRowDialog,
  BookSheetDialog,
  CompareResultDialog,
  MissingMaterialsDialog,
} from './IndustrialCostRecountSheetDialogs';
import IndustrialCostRecountRowDialog from './IndustrialCostRecountRowDialog';
import type { RecountRowSave } from './IndustrialCostRecountRowDialog';
import IndustrialCostRecountTablet from './IndustrialCostRecountTablet';
import RecountSheetCell from './IndustrialCostRecountSheetCell';
import type { CellMove } from './IndustrialCostRecountSheetCell';

const MUTED = '#94a3b8';
const AMBER = '#fde68a';
const HEADER_BG = '#1a2536';
const BUTTON = { textTransform: 'none', fontWeight: 800 };
const PANEL = { bgcolor: '#151f30', border: '1px solid rgba(148,163,184,0.15)', borderRadius: 2.5, boxShadow: 'none' };
// IND-047: primero proveedor y material; los seis valores se reparten el resto.
const COLUMNS: { key: SortKey; title: string; width?: number | string; align?: 'right' | 'center' }[] = [
  { key: 'row', title: 'FILA', width: 52 },
  { key: 'supplier', title: 'PROVEEDOR', width: 190 },
  { key: 'material', title: 'MATERIAL' },
  { key: 'previous', title: 'MES ANTERIOR', align: 'right' },
  { key: 'actual', title: 'ACTUAL', align: 'center' },
  { key: 'purchases', title: 'COMPRAS', align: 'right' },
  { key: 'consumption', title: 'CONSUMO', align: 'right' },
  { key: 'price', title: 'PRECIO €/kg', align: 'right' },
  { key: 'value', title: 'VALOR €', align: 'right' },
];
const PAGE_BG = '#081120';
// IND-049: «Estado: Todas ▾».
const STATUS_OPTIONS: [RecountFilter, string][] = [
  ['all', 'Todas'],
  ['pending', 'Pendientes'],
  ['counted', 'Contadas'],
  ['suggested', 'Con sugerencias'],
  ['manual', 'Cambiadas a mano'],
  ['invalid', 'Código no válido'],
  ['no_code', 'Proveedor sin código'],
];
// Las casillas que se escriben en la fila, en su orden de izquierda a derecha.
const CELL_FIELDS: RecountCellField[] = ['current_qty', 'purchases_qty', 'price'];
const euros = esNumberFormat({ maximumFractionDigits: 0 });
const price = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 4 });
const percent = esNumberFormat({ maximumFractionDigits: 2 });

type SortKey = 'row' | 'material' | 'supplier' | 'previous' | 'actual' | 'purchases' | 'consumption' | 'price' | 'value';

function queryKey(year: number, month: number) {
  return ['industrial-cost-recount-sheet', year, month];
}

function lastMonths(today: Date): { year: number; month: number }[] {
  return Array.from({ length: 13 }, (_, index) => {
    const day = new Date(today.getFullYear(), today.getMonth() - index, 1);
    return { year: day.getFullYear(), month: day.getMonth() + 1 };
  });
}

function shortDay(value: string): string {
  const [, month, day] = value.slice(0, 10).split('-');
  return `${day}/${month}`;
}

function fmtPrice(value: number | null): string {
  return value === null ? '—' : price.format(value);
}

/** IND-049: «Ir a»: la línea de inicio de la tabla queda bajo la cabecera fija. */
function scrollToTable(kind: RecountTableKind, offset: number): boolean {
  const start = document.getElementById(`recount-start-${kind}`);
  if (!start) return false;
  window.scrollTo({ top: start.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
  return true;
}

function sortValue(row: RecountSheetRow, key: SortKey | null): SortValue {
  if (key === 'row') return row.position;
  if (key === 'material') return row.reference ?? row.material_code;
  if (key === 'supplier') return row.supplier;
  if (key === 'previous') return row.previous_qty;
  if (key === 'actual') return row.current_qty;
  if (key === 'purchases') return row.purchases_qty;
  if (key === 'consumption') return rowConsumption(row);
  if (key === 'price') return row.price;
  return rowValue(row);
}

export default function IndustrialCostRecountSheetView({
  canManageImports,
  onOpenSuppliers,
}: {
  canManageImports: boolean;
  onOpenSuppliers: () => void;
}) {
  const months = useMemo(() => lastMonths(new Date()), []);
  // Por defecto, el último mes cerrado: el que se está contando.
  const [selected, setSelected] = useState(`${months[1].year}-${months[1].month}`);
  const [year, month] = selected.split('-').map(Number);
  const query = useQuery({ queryKey: queryKey(year, month), queryFn: () => getRecountSheet(year, month) });

  return (
    <Box sx={{ p: 2, minWidth: 0 }}>
      {query.isError && !query.data ? (
        <DetailError error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : !query.data ? (
        <DetailLoading height={360} />
      ) : (
        <SheetContent
          key={selected}
          year={year}
          month={month}
          view={query.data}
          months={months}
          selected={selected}
          onSelect={setSelected}
          canManageImports={canManageImports}
          onOpenSuppliers={onOpenSuppliers}
        />
      )}
    </Box>
  );
}

function SheetContent({
  year,
  month,
  view,
  months,
  selected,
  onSelect,
  canManageImports,
  onOpenSuppliers,
}: {
  year: number;
  month: number;
  view: RecountSheetView;
  months: { year: number; month: number }[];
  selected: string;
  onSelect: (value: string) => void;
  canManageImports: boolean;
  onOpenSuppliers: () => void;
}) {
  const queryClient = useQueryClient();
  const key = queryKey(year, month);
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  // IND-046: un proveedor escrito a mano sale al momento en «Proveedores del recuento».
  const refreshSuppliers = () => queryClient.invalidateQueries({ queryKey: ['industrial-cost-recount-suppliers'] });
  const monthName = MONTH_NAMES[month - 1].toLowerCase();
  const suggestedSheet = `STP-MP-${MONTH_NAMES[month - 1].toUpperCase()}`;
  const [dialog, setDialog] = useState<'prepare' | 'compare' | 'add' | 'missing' | null>(null);
  const [adding, setAdding] = useState<RecountMissingMaterial | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [compared, setCompared] = useState<RecountCompareResult | null>(null);
  const [using, setUsing] = useState<'purchases_qty' | 'price' | null>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<RecountFilter>('all');
  const [toolbar, setToolbar] = useState<HTMLDivElement | null>(null);
  const toolbarHeight = useElementHeight(toolbar);
  const [thead, setThead] = useState<HTMLTableSectionElement | null>(null);
  const theadHeight = useElementHeight(thead);
  const [activeTable, setActiveTable] = useState<RecountTableKind | null>(null);
  const [pendingJump, setPendingJump] = useState<RecountTableKind | null>(null);
  const [tableBox, setTableBox] = useState<HTMLDivElement | null>(null);
  const tableWidth = useElementSize(tableBox).width;
  // MATERIAL: mínimo 240 px y crece poco; los seis valores se reparten el resto.
  const materialWidth = Math.round(Math.min(340, Math.max(240, tableWidth * 0.24)));
  const [search, setSearch] = useState('');
  const sort = useTableSort<SortKey>(null, 'desc');

  // IND-049: guardado de cada casilla al salir de ella (ver recountCellSaver).
  const applySavedRef = useRef<(saved: RecountSheetRow, field?: RecountCellField) => void>(() => undefined);
  const [cellStates, setCellStates] = useState<Record<string, RecountCellState>>({});
  const [saver] = useState(() => new RecountCellSaver<RecountSheetRow>(
    (item) => saveRecountCell(item.rowId, item.field, item.value, item.source),
    {
      onState: (stateKey, state) => setCellStates((current) => {
        const next = { ...current };
        if (state) next[stateKey] = state;
        else delete next[stateKey];
        return next;
      }),
      onSaved: (saved, item) => applySavedRef.current(saved, item.field),
    },
  ));
  useEffect(() => {
    const retry = () => saver.retryOffline();
    const timer = window.setInterval(retry, 5_000);
    window.addEventListener('online', retry);
    // Aviso al salir de la página con algo sin guardar.
    const leaving = (event: BeforeUnloadEvent) => {
      if (!saver.hasUnsaved()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', leaving);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', retry);
      window.removeEventListener('beforeunload', leaving);
    };
  }, [saver]);

  // «Ir a»: se ilumina la tabla en la que se está al bajar.
  const stickyOffset = toolbarHeight + theadHeight;
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      let current: RecountTableKind | null = null;
      // Al final de la página, la última tabla que se ve aunque no llegue arriba.
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      for (const table of RECOUNT_TABLES) {
        const start = document.getElementById(`recount-start-${table.kind}`);
        const top = start?.getBoundingClientRect().top;
        if (top !== undefined && (current === null || top <= stickyOffset + 8 || (atBottom && top < window.innerHeight))) current = table.kind;
      }
      setActiveTable(current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [stickyOffset, view.rows, filter, search, sort.key, sort.direction]);
  useEffect(() => {
    if (pendingJump && scrollToTable(pendingJump, stickyOffset)) setPendingJump(null);
  });
  // IND-042: en pantallas táctiles se elige sola; se puede forzar con el botón.
  const [tablet, setTablet] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false);
  const contextQuery = useQuery({
    queryKey: ['industrial-cost-context'],
    queryFn: getIndustrialCostContext,
    retry: false,
  });

  const prepare = useMutation({
    mutationFn: ({ file, sheetName }: { file: File; sheetName: string }) =>
      prepareRecountSheetFromBook(year, month, file, sheetName, Boolean(view.sheet)),
    onSuccess: () => {
      setDialog(null);
      void refresh();
    },
  });
  const fromPrevious = useMutation({ mutationFn: () => prepareRecountSheetFromPrevious(year, month), onSuccess: refresh });
  const compare = useMutation({
    mutationFn: ({ file, sheetName }: { file: File; sheetName: string }) => compareRecountSheet(year, month, file, sheetName),
    onSuccess: (result) => {
      setDialog(null);
      setCompared(result);
    },
  });
  // Con «field», solo esa casilla: dos casillas de la misma fila se guardan a la
  // vez y la respuesta de una no debe pisar la otra.
  const applySaved = (saved: RecountSheetRow, field?: RecountCellField) => {
    const merge = (row: RecountSheetRow) =>
      field
        ? { ...row, [field]: saved[field], origins: { ...row.origins, [field]: saved.origins[field] } }
        : mergeSavedRow(row, saved);
    queryClient.setQueryData<RecountSheetView>(key, (current) =>
      current && { ...current, rows: current.rows.map((row) => (row.id === saved.id ? merge(row) : row)) });
  };
  applySavedRef.current = applySaved;
  const editRow = useMutation({
    mutationFn: async ({ rowId, save }: { rowId: string; save: RecountRowSave; nextId: string | null }) => {
      // Lo guardado aquí manda: un reintento antiguo de esa casilla no debe pisarlo.
      for (const [field] of save.changes) {
        if ((CELL_FIELDS as string[]).includes(field)) saver.discard(rowId, field as RecountCellField);
      }
      for (const [field, value, source] of save.changes) applySaved(await saveRecountCell(rowId, field, value, source));
      if (save.code) await saveRecountSupplierCode(save.code.supplier, save.code.value);
    },
    onSuccess: async (_, { nextId }) => {
      void refreshSuppliers();
      void queryClient.invalidateQueries({ queryKey: ['industrial-cost-recount-estimates', year, month] });
      // La fila siguiente debe abrirse ya con los códigos nuevos.
      await refresh();
      setEditingId(nextId);
    },
  });
  const addRow = useMutation({
    mutationFn: (draft: RecountRowDraft) => addRecountRow(year, month, draft),
    onSuccess: () => {
      setDialog(adding ? 'missing' : null);
      setAdding(null);
      void refresh();
      void refreshSuppliers();
    },
  });
  const removeRow = useMutation({
    mutationFn: deleteRecountRow,
    onSuccess: () => {
      setEditingId(null);
      void refresh();
      void refreshSuppliers();
    },
  });
  const dismiss = useMutation({
    mutationFn: (item: RecountMissingMaterial) => dismissRecountMaterial(year, month, item.key_code, item.delivery_key),
    onSuccess: refresh,
  });
  const suggestions = useMutation({
    mutationFn: (field: 'purchases_qty' | 'price') => applyRecountSuggestions(year, month, field),
    onSuccess: refresh,
    onSettled: () => setUsing(null),
  });
  const exportExcel = useMutation({
    mutationFn: () => exportRecountSheet(year, month),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${view.sheet?.sheet_name ?? suggestedSheet}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const closeMenuAnd = (action: () => void) => () => {
    setMenu(null);
    action();
  };

  const header = (
    <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" useFlexGap>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Inventario · en pruebas, el libro sigue mandando</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#f8fafc' }}>
          {`Hoja de recuento · ${monthName} ${year}`}
          {view.sheet ? (
            <Typography component="span" sx={{ color: MUTED, fontWeight: 600, ml: 1, fontSize: 14 }}>
              {`anterior ${shortDay(view.sheet.previous_date)} → actual ${shortDay(view.sheet.current_date_value)}`}
            </Typography>
          ) : null}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onSelect(event.target.value)}
          sx={{ minWidth: 160 }}
          aria-label="Mes de la hoja"
        >
          {months.map((item) => (
            <MenuItem key={`${item.year}-${item.month}`} value={`${item.year}-${item.month}`}>
              {`${MONTH_NAMES[item.month - 1]} ${item.year}`}
            </MenuItem>
          ))}
        </TextField>
        {view.sheet ? (
          <>
            <Button variant="outlined" onClick={(event: MouseEvent<HTMLElement>) => setMenu(event.currentTarget)} sx={{ ...BUTTON, height: 40 }}>
              Más ▾
            </Button>
            <Button variant="contained" onClick={() => setTablet(true)} sx={{ ...BUTTON, height: 40 }}>Contar con tablet</Button>
          </>
        ) : null}
      </Stack>
    </Stack>
  );

  if (!view.sheet) {
    return (
      <Stack spacing={1.5}>
        {header}
        <Paper sx={{ ...PANEL, p: 2 }}>
          <Stack spacing={1.25} alignItems="flex-start">
            <Typography sx={{ fontWeight: 800 }}>{`Aún no hay hoja de ${monthName}.`}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canManageImports ? (
                <Button variant="contained" onClick={() => setDialog('prepare')} sx={BUTTON}>Preparar desde el libro</Button>
              ) : null}
              {view.has_previous ? (
                <Button variant="outlined" disabled={fromPrevious.isPending} onClick={() => fromPrevious.mutate()} sx={BUTTON}>
                  Preparar desde la hoja anterior de PROA
                </Button>
              ) : null}
            </Stack>
            {fromPrevious.isError ? <Alert severity="error">No se pudo preparar. Inténtalo de nuevo.</Alert> : null}
            {!canManageImports && !view.has_previous ? (
              <Typography sx={{ color: MUTED, fontSize: 13 }}>Preparar desde el libro necesita el permiso de importar datos.</Typography>
            ) : null}
          </Stack>
        </Paper>
        {dialog === 'prepare' ? (
          <BookSheetDialog
            title={`Preparar la hoja de ${monthName}`}
            actionLabel="Preparar"
            suggestedSheet={suggestedSheet}
            busy={prepare.isPending}
            error={prepare.isError ? bookErrorText(prepare.error) : null}
            onClose={() => setDialog(null)}
            onSubmit={(file, sheetName) => prepare.mutate({ file, sheetName })}
          />
        ) : null}
      </Stack>
    );
  }

  const summary = sheetSummary(view.rows);
  const loss = replaceLoss(view.rows);
  const rows0 = filterSheetRows(view.rows, filter, search);
  const rows = sort.key ? sortRows(rows0, (row) => sortValue(row, sort.key), sort.direction) : rows0;
  const usingCount = using ? suggestionTargets(view.rows, using).length : 0;
  const purchaseTargets = suggestionTargets(view.rows, 'purchases_qty').length;
  const priceTargets = suggestionTargets(view.rows, 'price').length;
  const noCode = suppliersWithoutCode(view.rows);
  const rowNumbers = new Map(view.rows.map((row, index) => [row.id, index + 1]));
  const invalidCodes = view.rows.filter((row) => row.invalid_code).length;
  const editing = view.rows.find((row) => row.id === editingId) ?? null;
  // IND-048: en el orden de la hoja, una línea antes de cada tabla del libro.
  const natural = !sort.key || (sort.key === 'row' && sort.direction === 'asc');
  const starts = natural ? tableStarts(rows) : new Map();
  const tableCounts = new Map(RECOUNT_TABLES.map((table) => [table.kind, view.rows.filter((row) => tableOf(row).kind === table.kind).length]));
  // «Anterior / Siguiente» siguen lo que se ve en la tabla.
  const editingIndex = rows.findIndex((row) => row.id === editingId);
  const neighbour = (step: number) => rows[editingIndex + step]?.id ?? null;

  // IND-049: «Ir a» salta, no filtra. Ordenada por otra columna o con la tabla
  // oculta por la búsqueda, vuelve al orden de la hoja y sin filtros.
  const goToTable = (kind: RecountTableKind) => {
    if (!natural) {
      if (sort.key === 'row') sort.toggle('row');
      else sort.toggle('row', 'asc');
      if (!rows0.some((row) => tableOf(row).kind === kind)) {
        setFilter('all');
        setSearch('');
      }
      setPendingJump(kind);
    } else if (scrollToTable(kind, stickyOffset)) {
      setActiveTable(kind);
    } else {
      setFilter('all');
      setSearch('');
      setPendingJump(kind);
    }
  };

  const commitCell = (row: RecountSheetRow, field: RecountCellField, text: string, fromSuggestion: boolean) => {
    const state = saver.state(cellKey(row.id, field));
    // Lo mismo que ya se está guardando o esperando no se envía otra vez (un
    // rechazado sí: volver a confirmarlo es reintentar).
    if (!fromSuggestion && state && (state.status === 'saving' || state.status === 'offline') && state.text === text) return;
    const value = parseCellText(text, field);
    if (value === 'invalid') {
      saver.invalid(row.id, field, text);
      return;
    }
    // Volver al valor de PROA: sin nada en marcha, no hay nada que guardar; con
    // un guardado en marcha, se guarda también la vuelta atrás, después.
    if (!fromSuggestion && value === row[field] && !saver.busy(row.id, field)) {
      saver.discard(row.id, field);
      return;
    }
    saver.save({ rowId: row.id, field, value, source: fromSuggestion ? 'sugerencia' : 'tecleado', text });
  };
  // Enter / flechas: misma columna; Tab: a la derecha / izquierda, y de ahí a la fila siguiente.
  const discardCell = (row: RecountSheetRow, field: RecountCellField) => {
    if (saver.busy(row.id, field)) commitCell(row, field, kgDraft(row[field]), false);
    else saver.discard(row.id, field);
  };
  // Con algo sin guardar, cambiar de mes o de pantalla se confirma antes.
  const leaveSheet = (action: () => void) => () => {
    if (saver.hasUnsaved() && !window.confirm('Hay valores sin guardar en esta hoja. Si sales ahora, se pierden. ¿Salir igualmente?')) return;
    action();
  };
  const moveCell = (index: number, column: number, move: CellMove): boolean => {
    let rowIndex = index;
    let col = column;
    for (let step = 0; step < rows.length * CELL_FIELDS.length; step += 1) {
      if (move.columns) {
        col += move.columns;
        if (col >= CELL_FIELDS.length) {
          col = 0;
          rowIndex += 1;
        } else if (col < 0) {
          col = CELL_FIELDS.length - 1;
          rowIndex -= 1;
        }
      } else {
        rowIndex += move.rows;
      }
      if (rowIndex < 0 || rowIndex >= rows.length) return false;
      const target = document.querySelector<HTMLInputElement>(`[data-cell="${rowIndex}:${col}"]`);
      if (target) {
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: 'nearest' });
        return true;
      }
    }
    return false;
  };

  const chip = (color: string) => ({
    height: 30,
    px: 1.25,
    minWidth: 0,
    borderRadius: 2,
    border: `1px solid rgba(${color},0.35)`,
    bgcolor: `rgba(${color},0.08)`,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
  });

  return (
    <Stack spacing={1.25}>
      {/* IND-049: cabecera en tres líneas, fija arriba con la fila de títulos de columna. */}
      <Stack
        ref={setToolbar}
        spacing={1}
        sx={{ position: 'sticky', top: 0, zIndex: 4, bgcolor: PAGE_BG, pt: 1, pb: 1, borderBottom: '1px solid rgba(148,163,184,0.14)' }}
      >
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={1.25} alignItems="baseline" sx={{ minWidth: 0 }}>
            <Typography component="h1" noWrap sx={{ fontSize: 22, fontWeight: 800, color: '#f8fafc', flexShrink: 0 }}>
              {`Hoja de recuento · ${monthName} ${year}`}
            </Typography>
            <Typography noWrap sx={{ fontSize: 13, color: MUTED, minWidth: 0 }}>
              {`anterior ${shortDay(view.sheet.previous_date)} → actual ${shortDay(view.sheet.current_date_value)} · en pruebas, el libro sigue mandando`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <TextField
              select
              size="small"
              value={selected}
              onChange={(event) => leaveSheet(() => onSelect(event.target.value))()}
              sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 38, fontSize: 13 } }}
              aria-label="Mes de la hoja"
            >
              {months.map((item) => (
                <MenuItem key={`${item.year}-${item.month}`} value={`${item.year}-${item.month}`}>
                  {`${MONTH_NAMES[item.month - 1]} ${item.year}`}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={(event: MouseEvent<HTMLElement>) => setMenu(event.currentTarget)} sx={{ ...BUTTON, height: 38 }}>
              Más ▾
            </Button>
            <Button variant="contained" onClick={() => setTablet(true)} sx={{ ...BUTTON, height: 38 }}>Contar con tablet</Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={1.75} alignItems="center" sx={{ fontSize: 14, color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', minWidth: 0 }}>
            <span><strong style={{ color: '#f8fafc' }}>{summary.counted}</strong>{' de '}<strong style={{ color: '#f8fafc' }}>{summary.rows}</strong>{' contadas'}</span>
            <Box role="progressbar" aria-valuenow={summary.counted} aria-valuemin={0} aria-valuemax={summary.rows} sx={{ width: 160, height: 6, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.2)', flexShrink: 0 }}>
              <Box sx={{ width: `${summary.rows ? (summary.counted / summary.rows) * 100 : 0}%`, height: 6, borderRadius: 999, bgcolor: '#22d3ee' }} />
            </Box>
            <span>{'valor '}<strong style={{ color: '#f8fafc' }}>{`${euros.format(summary.value)} €`}</strong></span>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            {invalidCodes ? (
              <Button onClick={() => setFilter('invalid')} sx={{ ...chip('251,191,36'), color: AMBER }}>
                {`⚠ ${invalidCodes} ${invalidCodes === 1 ? 'código no válido' : 'códigos no válidos'}`}
              </Button>
            ) : null}
            {view.missing.length ? (
              <Button onClick={() => setDialog('missing')} sx={{ ...chip('34,211,238'), color: '#67e8f9' }}>
                {`${view.missing.length} ${view.missing.length === 1 ? 'material' : 'materiales'} por albarán · Revisar`}
              </Button>
            ) : null}
            {noCode ? (
              canManageImports ? (
                <Button onClick={leaveSheet(onOpenSuppliers)} sx={{ ...chip('251,191,36'), color: AMBER }}>
                  {`${noCode} ${noCode === 1 ? 'proveedor' : 'proveedores'} sin código · Poner código`}
                </Button>
              ) : (
                <Box sx={{ ...chip('251,191,36'), color: AMBER, display: 'inline-flex', alignItems: 'center' }}>
                  {`${noCode} ${noCode === 1 ? 'proveedor' : 'proveedores'} sin código`}
                </Box>
              )
            ) : null}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: MUTED, mr: 0.5 }}>Ir a:</Typography>
            {RECOUNT_TABLES.filter((table) => tableCounts.get(table.kind)).map((table) => {
              const active = activeTable === table.kind;
              return (
                <Button
                  key={table.kind}
                  aria-current={active ? 'location' : undefined}
                  onClick={() => goToTable(table.kind)}
                  sx={{
                    height: 32,
                    px: 1.5,
                    minWidth: 0,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    color: table.color,
                    border: `1px solid ${active ? table.color : 'rgba(148,163,184,0.25)'}`,
                    bgcolor: active ? `${table.color}29` : 'transparent',
                  }}
                >
                  {`● ${table.label} · ${tableCounts.get(table.kind)}`}
                </Button>
              );
            })}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <TextField
              size="small"
              type="search"
              placeholder="Buscar proveedor, material o código"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ width: 260, '& .MuiInputBase-root': { height: 32, fontSize: 13 } }}
            />
            <TextField
              select
              size="small"
              value={filter}
              onChange={(event) => setFilter(event.target.value as RecountFilter)}
              aria-label="Estado"
              SelectProps={{ renderValue: (value) => `Estado: ${STATUS_OPTIONS.find(([option]) => option === value)?.[1] ?? 'Todas'}` }}
              sx={{ minWidth: 150, '& .MuiInputBase-root': { height: 32, fontSize: 13 } }}
            >
              {STATUS_OPTIONS.map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem disabled={exportExcel.isPending} onClick={closeMenuAnd(() => exportExcel.mutate())}>Exportar a Excel</MenuItem>
        <MenuItem onClick={closeMenuAnd(() => setDialog('add'))}>Añadir fila</MenuItem>
        {canManageImports ? <MenuItem onClick={closeMenuAnd(() => setDialog('prepare'))}>Preparar desde el libro</MenuItem> : null}
        {canManageImports ? <MenuItem onClick={closeMenuAnd(() => setDialog('compare'))}>Comparar con el libro</MenuItem> : null}
        <MenuItem disabled={!purchaseTargets} onClick={closeMenuAnd(() => setUsing('purchases_qty'))}>Usar las sugerencias de COMPRAS</MenuItem>
        <MenuItem disabled={!priceTargets} onClick={closeMenuAnd(() => setUsing('price'))}>Usar las sugerencias de PRECIO</MenuItem>
      </Menu>
      </Stack>
      {exportExcel.isError ? <Alert severity="error">No se pudo exportar. Inténtalo de nuevo.</Alert> : null}
      {suggestions.isError || dismiss.isError ? <Alert severity="error">No se pudo hacer. Inténtalo de nuevo.</Alert> : null}

      {/* IND-048: sin caja con scroll interno; la cabecera queda fija bajo la barra de filtros. */}
      <Paper ref={setTableBox} sx={{ ...PANEL, overflowX: 'clip', mt: '4px !important' }}>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { px: 1, fontSize: 13 },
            // Al moverse con el teclado, la casilla no queda bajo la cabecera fija.
            '& input[data-cell]': { scrollMarginTop: `${stickyOffset + 8}px`, scrollMarginBottom: '16px' },
          }}
        >
          <colgroup>
            {COLUMNS.map((column) => {
              const width = column.key === 'material' ? materialWidth : column.width;
              return <col key={column.key} style={width ? { width } : undefined} />;
            })}
          </colgroup>
          <TableHead ref={setThead}>
            <TableRow>
              {COLUMNS.map((column) => (
                <SortableHeaderCell
                  key={column.key}
                  label={column.title}
                  columnKey={column.key}
                  sort={sort}
                  align={column.align ?? 'left'}
                  firstDirection={column.align ? 'desc' : 'asc'}
                  sx={{
                    position: 'sticky',
                    top: toolbarHeight,
                    zIndex: 3,
                    bgcolor: HEADER_BG,
                    fontSize: '11px !important',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    // Sin scroll lateral: la flecha solo ocupa sitio en la columna ordenada.
                    '& .MuiTableSortLabel-root:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' },
                  }}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const start = starts.get(row.id);
              return (
                <Fragment key={row.id}>
                  {start ? (
                    <TableRow id={`recount-start-${start.kind}`}>
                      <TableCell
                        colSpan={COLUMNS.length}
                        sx={{
                          py: 0.5,
                          fontSize: '12px !important',
                          fontWeight: 800,
                          color: start.color,
                          bgcolor: `${start.color}14`,
                          boxShadow: `inset 4px 0 0 ${start.color}`,
                        }}
                      >
                        {`${start.label} · ${start.count} ${start.count === 1 ? 'fila' : 'filas'}`}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  <SheetRow
                    row={row}
                    index={index}
                    number={rowNumbers.get(row.id) ?? 0}
                    onEdit={() => {
                      editRow.reset();
                      removeRow.reset();
                      setEditingId(row.id);
                    }}
                    cellState={(field) => cellStates[cellKey(row.id, field)]}
                    onCommit={(field, text, fromSuggestion) => commitCell(row, field, text, fromSuggestion)}
                    onMove={(field, move) => moveCell(index, CELL_FIELDS.indexOf(field), move)}
                    onDiscard={(field) => discardCell(row, field)}
                    onRetry={(field) => saver.retry(row.id, field)}
                    onOpenSuppliers={canManageImports ? leaveSheet(onOpenSuppliers) : null}
                  />
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      <Typography sx={{ color: '#64748b', fontSize: 12 }}>
        Mismo orden que el Excel, fila a fila. ACTUAL, COMPRAS y PRECIO se escriben en la fila: Enter baja, Tab va a la derecha, Esc deshace y Ctrl+Enter o doble clic usan la sugerencia (azul). El resto, al pulsar la fila.
      </Typography>

      <Dialog open={using !== null} onClose={() => setUsing(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{using === 'price' ? 'Usar las sugerencias de PRECIO' : 'Usar las sugerencias de COMPRAS'}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14 }}>{`Cambia ${usingCount} celdas. Las tecleadas a mano no se tocan.`}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsing(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={suggestions.isPending || !usingCount}
            onClick={() => using && suggestions.mutate(using)}
            sx={BUTTON}
          >
            {`Cambiar ${usingCount}`}
          </Button>
        </DialogActions>
      </Dialog>

      {editing ? (
        <IndustrialCostRecountRowDialog
          key={editing.id}
          row={editing}
          number={rowNumbers.get(editing.id) ?? 0}
          total={view.rows.length}
          year={year}
          month={month}
          monthName={monthName}
          previousDay={shortDay(view.sheet.previous_date)}
          currentDay={shortDay(view.sheet.current_date_value)}
          canEditCode={canManageImports}
          busy={editRow.isPending || removeRow.isPending}
          failed={editRow.isError || removeRow.isError}
          hasPrevious={neighbour(-1) !== null}
          hasNext={neighbour(1) !== null}
          onMove={(step) => {
            editRow.reset();
            setEditingId(neighbour(step));
          }}
          onClose={() => setEditingId(null)}
          onSave={(save, goNext) => editRow.mutate({ rowId: editing.id, save, nextId: goNext ? neighbour(1) : null })}
          onDelete={() => removeRow.mutate(editing.id)}
        />
      ) : null}
      {dialog === 'prepare' ? (
        <BookSheetDialog
          title={`Preparar la hoja de ${monthName}`}
          actionLabel="Sustituir la hoja"
          warning={
            loss.counted || loss.typed
              ? `Ya hay hoja de este mes y se sustituye entera: se borrarán ${loss.counted} ${loss.counted === 1 ? 'valor contado' : 'valores contados'} y ${loss.typed} ${loss.typed === 1 ? 'cambio a mano' : 'cambios a mano'} de ${monthName}.`
              : `Ya hay hoja de este mes y se sustituye entera. No tiene nada contado ni cambiado a mano.`
          }
          suggestedSheet={suggestedSheet}
          busy={prepare.isPending}
          error={prepare.isError ? bookErrorText(prepare.error) : null}
          onClose={() => setDialog(null)}
          onSubmit={(file, sheetName) => prepare.mutate({ file, sheetName })}
        />
      ) : null}
      {dialog === 'compare' ? (
        <BookSheetDialog
          title="Comparar con el libro"
          actionLabel="Comparar"
          suggestedSheet={suggestedSheet}
          busy={compare.isPending}
          error={compare.isError ? bookErrorText(compare.error) : null}
          onClose={() => setDialog(null)}
          onSubmit={(file, sheetName) => compare.mutate({ file, sheetName })}
        />
      ) : null}
      {compared ? (
        <CompareResultDialog sheetName={view.sheet.sheet_name} result={compared} onClose={() => setCompared(null)} />
      ) : null}
      {dialog === 'add' || adding ? (
        <AddRowDialog
          key={adding ? `${adding.key_code}|${adding.delivery_key}` : 'hand'}
          rows={view.rows}
          title={adding ? 'Añadir fila propuesta' : 'Añadir fila'}
          requireDetails={Boolean(adding)}
          initial={adding ? {
            material_code: adding.material_code,
            reference: adding.reference ?? '',
            supplier: adding.supplier ?? '',
            supplier_code: adding.supplier_code ?? '',
          } : undefined}
          busy={addRow.isPending}
          failed={addRow.isError}
          onClose={() => {
            setDialog(adding ? 'missing' : null);
            setAdding(null);
          }}
          onSave={(draft) => addRow.mutate({ ...draft, delivery_key: adding?.delivery_key ?? null })}
        />
      ) : null}
      {dialog === 'missing' ? (
        <MissingMaterialsDialog
          monthName={monthName}
          items={view.missing}
          busy={dismiss.isPending}
          onClose={() => setDialog(null)}
          onAdd={(item) => {
            setDialog(null);
            setAdding(item);
          }}
          onDismiss={(item) => dismiss.mutate(item)}
        />
      ) : null}
      {tablet && contextQuery.data ? (
        <IndustrialCostRecountTablet
          // IND-058: otro usuario = otra tablet (su cola y su fila, nunca las del anterior).
          key={String(contextQuery.data.user_id)}
          userId={String(contextQuery.data.user_id)}
          year={year}
          month={month}
          view={view}
          onClose={() => setTablet(false)}
          onExport={() => exportExcel.mutate()}
          exporting={exportExcel.isPending}
          exportFailed={exportExcel.isError}
          onOpenSuppliers={canManageImports ? onOpenSuppliers : null}
        />
      ) : null}
    </Stack>
  );
}

const TAG = { fontSize: 11, px: 0.75, borderRadius: 1.5, bgcolor: 'rgba(148,163,184,0.14)', color: '#cbd5e1', whiteSpace: 'nowrap' };
const AUTO_TEXT = { color: MUTED, fontVariantNumeric: 'tabular-nums' };
const BLUE = '#67e8f9';

function SheetRow({
  row,
  index,
  number,
  onEdit,
  cellState,
  onCommit,
  onMove,
  onDiscard,
  onRetry,
  onOpenSuppliers,
}: {
  row: RecountSheetRow;
  index: number;
  number: number;
  onEdit: () => void;
  cellState: (field: RecountCellField) => RecountCellState | undefined;
  onCommit: (field: RecountCellField, text: string, fromSuggestion: boolean) => void;
  onMove: (field: RecountCellField, move: CellMove) => boolean;
  onDiscard: (field: RecountCellField) => void;
  onRetry: (field: RecountCellField) => void;
  onOpenSuppliers: (() => void) | null;
}) {
  const noCode = row.supplier_state === 'no_code';
  const table = tableOf(row);
  const typedText = (field: string) => (isTyped(row, field) ? { color: AMBER } : undefined);
  const shareTag = row.share !== null && row.share !== 1;
  const sameClass = (row.class_label ?? '').trim().toUpperCase() === (row.destination ?? '').trim().toUpperCase();
  const small = { fontSize: 11, lineHeight: 1.3 };
  const name = row.reference ?? row.material_code;
  const cell = (field: RecountCellField, title: string, format: (value: number) => string, suggestion: number | null) => (
    <RecountSheetCell
      cellId={`${index}:${CELL_FIELDS.indexOf(field)}`}
      label={`${title} de ${name}`}
      value={row[field]}
      format={format}
      suggestion={suggestion}
      typed={isTyped(row, field)}
      state={cellState(field)}
      onCommit={(text, fromSuggestion) => onCommit(field, text, fromSuggestion)}
      onMove={(move) => onMove(field, move)}
      onDiscard={() => onDiscard(field)}
      onRetry={() => onRetry(field)}
    />
  );

  return (
    <TableRow
      hover
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target === event.currentTarget) onEdit();
      }}
      aria-label={`Editar fila ${number}: ${name}`}
      sx={{ cursor: 'pointer', height: 48, '& .MuiTableCell-root': { py: 0.25 } }}
    >
      <TableCell
        title={table.label}
        sx={{ color: '#64748b', fontSize: '12px !important', fontVariantNumeric: 'tabular-nums', pl: '14px !important', boxShadow: `inset 4px 0 0 ${table.color}` }}
      >
        {number}
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Typography noWrap title={row.supplier ?? undefined} sx={{ fontSize: 14, fontWeight: 800, color: '#f8fafc', lineHeight: 1.3, ...typedText('supplier') }}>
          {row.supplier ?? '—'}
        </Typography>
        {noCode ? (
          onOpenSuppliers ? (
            <Link
              component="button"
              type="button"
              onClick={(event: MouseEvent) => {
                event.stopPropagation();
                onOpenSuppliers();
              }}
              sx={{ ...small, display: 'block', color: AMBER, textDecorationColor: 'rgba(253,230,138,0.5)' }}
            >
              sin código · poner
            </Link>
          ) : (
            <Typography sx={{ ...small, color: AMBER }}>sin código</Typography>
          )
        ) : (
          <Typography sx={{ ...small, color: BLUE, fontVariantNumeric: 'tabular-nums' }}>{row.supplier_code}</Typography>
        )}
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Typography noWrap title={row.reference ?? undefined} sx={{ fontSize: 14, fontWeight: 600, color: '#f8fafc', lineHeight: 1.3, ...typedText('reference') }}>
          {name}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ overflow: 'hidden' }}>
          <Typography noWrap sx={{ ...small, color: MUTED, flexShrink: 0, ...typedText('material_code') }}>{row.material_code}</Typography>
          {row.destination ? <Box sx={{ ...TAG, ...typedText('destination') }}>{row.destination}</Box> : null}
          {row.class_label && !sameClass ? <Box sx={{ ...TAG, ...typedText('class_label') }}>{row.class_label}</Box> : null}
          {shareTag ? <Box sx={{ ...TAG, bgcolor: 'rgba(251,191,36,0.14)', color: AMBER }}>{`% ${percent.format((row.share ?? 0) * 100)}`}</Box> : null}
          {row.invalid_code ? <Typography noWrap sx={{ ...small, color: AMBER }}>código no válido en el libro</Typography> : null}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ ...AUTO_TEXT, ...typedText('previous_qty') }}>{fmtQty(row.previous_qty)}</TableCell>
      <TableCell align="right" sx={{ cursor: 'default' }} onClick={(event) => event.stopPropagation()}>
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          {cell('current_qty', 'ACTUAL', (value) => fmtQty(value), null)}
          <Box component="span" sx={{ width: 16, flexShrink: 0, fontSize: 11, color: '#64748b' }}>
            {row.unit === 'unidades' ? 'ud' : 'kg'}
          </Box>
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ cursor: 'default' }} onClick={(event) => event.stopPropagation()}>
        {cell('purchases_qty', 'COMPRAS', (value) => fmtQty(value), row.purchases_suggested)}
      </TableCell>
      <TableCell align="right" sx={AUTO_TEXT}>{fmtQty(rowConsumption(row))}</TableCell>
      <TableCell align="right" sx={{ cursor: 'default' }} onClick={(event) => event.stopPropagation()}>
        {cell('price', 'PRECIO', (value) => fmtPrice(value), row.price_suggested)}
      </TableCell>
      <TableCell align="right" sx={AUTO_TEXT}>{(() => {
        const value = rowValue(row);
        return value === null ? '—' : euros.format(value);
      })()}</TableCell>
    </TableRow>
  );
}
